/* ============================================================================
 * useFilteredNodes.ts - Filtering and sorting logic for nodes
 * ============================================================================ */

import { useDeferredValue, useMemo } from 'react';
import type { ProjectData, ProjectNode, ProjectNodeWithComputed, ViewFilter } from '@/shared/types/projectData';
import type { User } from '@/features/auth/services/authService';
import { isReportNode } from '@/features/reports/utils/reportIdentity';
import { applyFiltersAndSearch, filterNodesByPermissions } from '@/features/projects/helpers/projectDataFilters';

interface UseFilteredNodesParams {
  projectData: ProjectData | null;
  filters: ViewFilter;
  searchQuery: string;
  sortBy: string | null;
  user: User | null;
  hasPermission: (resource: string, action: string) => boolean;
}

/**
 * Hook for filtering and sorting project nodes
 */
export function useFilteredNodes({
  projectData,
  filters,
  searchQuery,
  sortBy,
  user,
  hasPermission
}: UseFilteredNodesParams): ProjectNodeWithComputed[] {
  const deferredFilters = useDeferredValue(filters);
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const deferredSortBy = useDeferredValue(sortBy);

  return useMemo(() => {
    if (!projectData) return [];

    let nodes: ProjectNode[] = projectData.nodes.filter(node => {
      const maybeDeleted = node as ProjectNode & { _deleted?: boolean; isDeleted?: boolean };
      return !maybeDeleted._deleted && !maybeDeleted.isDeleted && !isReportNode(node);
    });

    if (user && user.role !== 'admin') {
      nodes = filterNodesByPermissions(nodes, hasPermission);
    }

    let projectedNodes = applyFiltersAndSearch(nodes, deferredFilters, deferredSearchQuery);

    // Apply sort
    if (deferredSortBy && deferredSortBy !== 'default') {
      projectedNodes = [...projectedNodes].sort((a, b) => {
        switch (deferredSortBy) {
          case 'title-asc':
            return (a.title || '').localeCompare(b.title || '');
          case 'title-desc':
            return (b.title || '').localeCompare(a.title || '');
          case 'priority-high': {
            const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
            return (priorityOrder[a.priority as keyof typeof priorityOrder] ?? 4) -
                   (priorityOrder[b.priority as keyof typeof priorityOrder] ?? 4);
          }
          case 'priority-low': {
            const priorityOrderReverse = { low: 0, medium: 1, high: 2, critical: 3 };
            return (priorityOrderReverse[a.priority as keyof typeof priorityOrderReverse] ?? 4) -
                   (priorityOrderReverse[b.priority as keyof typeof priorityOrderReverse] ?? 4);
          }
          case 'date-newest':
            return new Date(b.endDate || b.startDate || 0).getTime() -
                   new Date(a.endDate || a.startDate || 0).getTime();
          case 'date-oldest':
            return new Date(a.endDate || a.startDate || 0).getTime() -
                   new Date(b.endDate || b.startDate || 0).getTime();
          default:
            return 0;
        }
      });
    }

    return projectedNodes;
  }, [projectData, deferredFilters, deferredSearchQuery, deferredSortBy, user, hasPermission]);
}
